/**
 * Analytics provider for Facebook pixel.
 *
 * @param config - dictionary containing the information needed to create the FB pixel.
 * @param config.pixelId: The Facebook pixel ID, links it to the correct FB account.
 * @param config.identifyPayloadMapping: Maps our `identify()` data payload keys to the Facebook specific ones.
 * @param config.pageMeta: Information about the page events are being fired on. This data is sent for all `track()` events.
 * @returns {{NAMESPACE: string, loaded: (function(): boolean), identify: identify, initialize: initialize, page: page, track: track, config: *}}
 */
function facebookPixel(config) {
    const identifyPayloadMapping = config.identifyPayloadMapping;
    // Used to track whether a real or virtual page view needs to be sent.
    let pageViewSent = false;

    return {
        NAMESPACE: 'facebook-pixel',
        config: config,
        initialize: ({config}) => {
            if (!config.pixelId) {
                throw new Error('No pixel ID provided!')
            }
            !((f, b, e, v, n, t, s) => {
                if (!f.fbq) {
                    n = f.fbq = function () {
                        !n.callMethod ? n.queue.push(arguments) : n.callMethod.apply(n, arguments)
                    };
                    if (!f._fbq) f._fbq = n;
                    n.push = n;
                    n.loaded = !0;
                    n.version = '2.0';
                    n.queue = [];
                    t = b.createElement(e);
                    t.async = !0;
                    t.src = v;
                    s = b.getElementsByTagName(e)[0];
                    s.parentNode.insertBefore(t, s)
                }
            })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

            fbq('init', config.pixelId);
        },
        identify: ({payload, config}) => {
            fbq('init', config.pixelId, {
                uid: payload.userId
            });

            let userProperties = {};
            for (let [key, value] of Object.entries(payload.traits)) {
                if (key in identifyPayloadMapping) {
                    const convertedKey = identifyPayloadMapping[key];
                    userProperties[convertedKey] = value;
                } else {
                    userProperties[key] = value;
                }
            }

            if (userProperties) {
                fbq('setUserProperties', config.pixelId, userProperties);
            }
        },
        /**
         * Facebook implements their own page tracking for pushState changes. Per a blog post from 2017
         * you should be able to turn it off, but it disables the calling the track pageView event as well.
         * This means Facebook does not honor pageViewData for pushState PageViews so we send an extra
         * VirtualPageView that has the data.
         *
         * tl;dr For pushState page views Facebook gets a PageView with none of our data
         *  and a VirtualPageView with pageViewData.
         */
        page: ({payload}) => {
            const {properties} = payload;
            if (!pageViewSent) {
                fbq('track', 'PageView', properties);
                pageViewSent = true;
            } else {
                fbq('trackCustom', 'VirtualPageView', properties);
            }
        },
        track: ({payload}) => {
            const {properties, event} = payload;
            const eventData = JSON.parse(JSON.stringify(properties));

            switch (event) {
                case 'AddPaymentInfo':
                    if (eventData.products) {
                        let products = eventData.products;
                        eventData['product_count'] = products.length;
                        eventData['product_names'] = products.flatMap(product => product.name).join();
                    }
                    break;
                case 'AddToCart':
                    if (eventData.product) {
                        let product = eventData.product;
                        // Unwrap the product object into multiple `product_{field}` parameters
                        for (let [key, value] of Object.entries(product)) {
                            eventData[`product_${key}`] = value;
                        }
                        // Set the FB specific parameters.
                        eventData['content_ids'] = [product.id];
                        eventData['content_name'] = product.name;
                        eventData['content_type'] = `${product.type}: ${product.category}`;
                        eventData['value'] = product.price;

                        delete eventData['product'];
                    }
                    break;
                case 'ViewProducts':
                    if (eventData.products) {
                        let products = eventData.products;
                        eventData['product_count'] = products.length;
                        eventData['product_names'] = products.flatMap(product => product.name).join();
                    }
                    break;
                case 'PrintSubscriberRequest':
                    if (!Object.prototype.hasOwnProperty.call(eventData, 'is_print_subscriber')) {
                        // Don't send the event if they didn't check the print subscriber checkbox
                        return;
                    }
                    if (eventData.is_print_subscriber !== 'on') {
                        // Don't send the event if they didn't check the print subscriber checkbox
                        return;
                    }
                    break;
                case 'Purchase':
                    if (eventData.products) {
                        let products = eventData.products;
                        let productIds = [];
                        let runningTotal = 0;
                        for (let product of products) {
                            productIds.push(product.id);
                            runningTotal += product.price;
                        }

                        eventData['content_ids'] = productIds;
                        eventData['content_name'] = products[0].name;
                        eventData['content_type'] = `${products[0].type}: ${products[0].category}`;
                        eventData['currency'] = products[0].currency;
                        eventData['value'] = runningTotal;
                    }
                    break;
                case 'Search':
                    eventData['search_string'] = eventData.query;
                    break;
                case 'SecondStreetSignup':
                case 'MailchimpSignup': {
                    let emailSignupNames = "";
                    for (let [signupName, value] of Object.entries(properties)) {
                        if (value === 'on') {
                            if (emailSignupNames !== '') {
                                emailSignupNames += ',';
                            }
                            emailSignupNames += signupName;
                            // Don't send field data to FB
                            delete eventData[signupName];
                        }
                    }
                    eventData['newsletters'] = emailSignupNames;
                    break;
                }
                default:
                    break;
            }

            if (Object.prototype.hasOwnProperty.call(standardizedEvents, event)) {
                fbq('track', standardizedEvents[event], eventData);
            } else {
                fbq('trackCustom', event, eventData);
            }
        },
        loaded: () => {
            return !!window.fbq
        },
    }
}

export default facebookPixel

/**
 * A dictionary used to convert our common event names into platform specific standard event calls.
 *
 * AddPaymentInfo - User added payment information (ex. purchasing a subscription in the Order Tunnel)
 * AddToCart - User selects product.
 * CompleteRegistration - User submits a registration/sign-up form.
 * InitiateCheckout - User has entered the checkout flow (ex. prior to confirming subscription)
 * PageView - User opens a page.
 * Purchase - User completed checkout flow.
 * Search - User initiated search function.
 * StartTrial - User starts a free trial (ex. default rate card).
 * Subscribe - User subscribes to a service offered.
 * ViewContent - User views product details page.
 * */

const standardizedEvents = {
    "AddPaymentInfo": "AddPaymentInfo",
    "AddToCart": "AddToCart",
    "CompleteRegister": "CompleteRegistration",
    "InitiateCheckout": "InitiateCheckout",
    "PageView": "PageView",
    "Purchase": "Purchase",
    "Search": "Search",
    "StartTrial": "StartTrial",
    "Subscribe": "Subscribe",
    "ViewContent": "ViewContent"
};
